import React from "react";
import { NavigationType, useLocation, useNavigate, useNavigationType, useSearchParams } from "react-router-dom";
import { getParentRoutePath } from "shared/utils";

const noop = (x: string) => {
  return x;
};

export default function useNavigateWrapper() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const navigationType = useNavigationType();

  const navigateBack = React.useCallback(
    (cleanupQueryParamsBeforeNavigate: Function = noop) => {
      if (navigationType === NavigationType.Pop) {
        const cleanedSearchParams = cleanupQueryParamsBeforeNavigate?.(searchParams.toString());
        navigate({
          pathname: getParentRoutePath(location.pathname) || "/",
          search: cleanedSearchParams
        });
      } else {
        navigate(-1);
      }
    },
    [location.pathname, navigate, navigationType, searchParams]
  );

  /**
   * @method navigateToPreviousPath
   * @summary to be used when we need to use relative route (..) for going back
   * @description When we have a single path with multiple parts such as `/home/myinfo`, we refresh the page and then try to go back with `navigateBack`, it won't work as expected - it'll still keep `/home` in the url. This method is meant for such cases.
   * @see https://reactrouter.com/en/main/hooks/use-navigate#optionsrelative
   */
  const navigateToPreviousPath = React.useCallback(
    (cleanupQueryParamsBeforeNavigate?: Function) => {
      const cleanedSearchParams = cleanupQueryParamsBeforeNavigate?.(searchParams.toString());
      navigate({ pathname: "..", search: cleanedSearchParams ?? searchParams.toString() });
    },
    [navigate, searchParams]
  );

  return {
    navigateBack,
    navigate,
    navigateToPreviousPath
  };
}
